import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Stack,
  CircularProgress,
} from "@mui/material";
import { brand } from "../../theme/themePrimitives";
import upload from "../../assets/upload.svg";
import { useTranslation } from "../../context/TranslationContext";
import {
  AttributeKey,
  PageUid,
  useCustomisation,
} from "../../context/CustomisationContext";
import { useToast } from "../../context/ToastContext";
import { getFileNameFromUrl } from "../../utils/helpers";
import { EllipsisTooltip } from "../EllipsisTooltip/EllipsisTooltip";

type ImageType =
  | "favicon"
  | "landscape"
  | "square"
  | "backgroundImage"
  | "poweredByLogoLight"
  | "poweredByLogoDark";

interface ImageUploaderProps {
  type: ImageType;
  onImageResized: (resizedFile: File, previewUrl: string) => void;
  label?: string;
  description?: string;
  loading?: boolean;
  pageId?: PageUid;
}

// Per-type maximum image sizes (in bytes) and human-readable labels.
const IMAGE_MAX_SIZE_BYTES: Record<ImageType, number> = {
  favicon: 256 * 1024, // 256 KB
  landscape: 1 * 1024 * 1024, // 1 MB
  square: 1 * 1024 * 1024, // 1 MB
  backgroundImage: 3 * 1024 * 1024, // 3 MB
  poweredByLogoLight: 1 * 1024 * 1024, // 1 MB
  poweredByLogoDark: 1 * 1024 * 1024, // 1 MB
};

const IMAGE_MAX_SIZE_LABEL: Record<ImageType, string> = {
  favicon: "256 KB",
  landscape: "1 MB",
  square: "1 MB",
  backgroundImage: "3 MB",
  poweredByLogoLight: "1 MB",
  poweredByLogoDark: "1 MB",
};

/** Replaces spaces with underscores so filenames are safe for URLs (e.g. in CSS background-image). */
const sanitizeFileName = (name: string) => name.replace(/\s+/g, "_");

function getLogoPath(
  getCustomisationValue: (
    pageId: PageUid,
    section: "logoSettings",
    key: AttributeKey,
  ) => string | undefined,
  pageId: PageUid,
  type:
    | "favicon"
    | "landscape"
    | "square"
    | "poweredByLogoLight"
    | "poweredByLogoDark",
): string | undefined {
  if (type === "poweredByLogoLight") {
    return getCustomisationValue(
      pageId,
      "logoSettings",
      "loginPoweredByLogoLight",
    );
  }
  if (type === "poweredByLogoDark") {
    return getCustomisationValue(
      pageId,
      "logoSettings",
      "loginPoweredByLogoDark",
    );
  }
  if (type === "landscape" && pageId === "CERTIFICATES") {
    return getCustomisationValue(pageId, "logoSettings", "certificatesLogo");
  }
  const key =
    `${pageId.toLowerCase()}${type === "favicon" ? "Favicon" : type === "landscape" ? "LandscapeLogo" : "SquareLogo"}` as AttributeKey;
  return getCustomisationValue(pageId, "logoSettings", key);
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  type,
  onImageResized,
  label = "Upload Image",
  description,
  loading,
  pageId = "GPC",
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { getCustomisationValue } = useCustomisation();
  const { showToast } = useToast();

  const previewFromContext =
    type === "backgroundImage"
      ? getCustomisationValue(
          pageId,
          "backgroundSettings",
          `${pageId.toLowerCase()}BackgroundImage` as AttributeKey,
        )
      : getLogoPath(
          getCustomisationValue,
          pageId,
          type as
            | "favicon"
            | "landscape"
            | "square"
            | "poweredByLogoLight"
            | "poweredByLogoDark",
        );

  const previewFromContextUrl =
    previewFromContext != null && previewFromContext !== ""
      ? `/${previewFromContext}`
      : null;

  const [localUploadPreview, setLocalUploadPreview] = useState<string | null>(
    null,
  );
  const displayedPreview = localUploadPreview ?? previewFromContextUrl;

  useEffect(() => {
    setLocalUploadPreview(null);
  }, [pageId]);

  const { translations } = useTranslation();
  const logoTranslations = translations.translations["portal-design"].gpc;
  const imageTitle =
    type === "backgroundImage"
      ? previewFromContext
        ? getFileNameFromUrl(previewFromContext)
        : undefined
      : previewFromContext
        ? getFileNameFromUrl(previewFromContext)
        : undefined;

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const maxBytes = IMAGE_MAX_SIZE_BYTES[type];
      const maxLabel = IMAGE_MAX_SIZE_LABEL[type];
      if (file.size > maxBytes) {
        showToast(`Image size must be less than ${maxLabel}`, "error", 4000);
        e.target.value = "";
        return;
      }

      if (/[() %+#$&@[\]{},]/.test(file.name)) {
        showToast(
          "Upload failed. The filename contains unsupported characters. Please rename the file using accepted characters and try again.",
          "error",
        );
        e.target.value = "";
        return;
      }

      const sanitizedName = sanitizeFileName(file.name);
      const fileToUse =
        sanitizedName !== file.name
          ? new File([file], sanitizedName, { type: file.type })
          : file;
      const previewUrl = URL.createObjectURL(fileToUse);
      setLocalUploadPreview(previewUrl);
      onImageResized(fileToUse, previewUrl);
    } catch (err) {
      console.error("Error resizing image:", err);
    }
  };

  const handleButtonClick = () => {
    inputRef.current?.click();
  };

  return (
    <Stack spacing={1} alignItems="flex-start" width={200}>
      <Stack spacing={1} width={"100%"}>
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          {displayedPreview && (
            <Stack spacing={1} alignItems="center" sx={{ width: "100%" }}>
              <Box
                sx={{
                  position: "relative",
                  display: "inline-block",
                  mt: 1,
                  border: "1px solid #ccc",
                  borderRadius: 1,
                  maxWidth: 150,
                }}
              >
                <Box
                  component="img"
                  src={displayedPreview}
                  alt={`${type} preview`}
                  sx={{
                    display: "block",
                    width: "100%",
                    borderRadius: 1,
                  }}
                />
              </Box>
              {imageTitle && (
                <Typography
                  component="div"
                  textAlign={"center"}
                  sx={{
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <EllipsisTooltip title={imageTitle} maxWidth={200}>
                    {imageTitle}
                  </EllipsisTooltip>
                </Typography>
              )}
            </Stack>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography fontWeight="bold" fontSize={"16px"}>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
        {loading ? (
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Button
            sx={{
              px: 2,
              borderRadius: "12px",
              backgroundColor: `${brand[600]} !important`,
              color: "white",
              display: "flex",
              gap: "0.5rem",
              alignItems: "center",
              "&:hover": {
                backgroundColor: `${brand[700]} !important`,
              },
            }}
            onClick={handleButtonClick}
          >
            <img src={upload} alt="upload" />
            {logoTranslations?.uploadImageText}
          </Button>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleChange}
        />
      </Stack>
    </Stack>
  );
};
